<script setup lang="ts">

import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { sortBy } from '@shell/utils/sort';
import { _VIEW } from '@shell/config/query-params';
import { EpinioConfiguration, EpinioService, EPINIO_TYPES, EPINIO_APP_MANIFEST, EpinioAppBindings } from '../../types';
import Application from '../../models/applications';

interface Props {
  initialApplication?: Application;
  application: Application;
  mode: string;
  bindings?: EpinioAppBindings;
}

const props = defineProps<Props>();
const emit = defineEmits(['change']);

const store = useStore();

const t = store.getters['i18n/t'];

const values = ref({
  configurations: props.bindings?.configurations || [] as string[],
  services: props.bindings?.services.map((s: EpinioService) => s.id) || [] as string[],
});

const fetchData = async () => {
  await Promise.all([
    store.dispatch('epinio/findAll', { type: EPINIO_TYPES.CONFIGURATION }),
    store.dispatch('epinio/findAll', { type: EPINIO_TYPES.SERVICE_INSTANCE }),
  ]);
};

onMounted(fetchData);

// Computed
const namespacedServices = computed(() =>
  (store.getters['epinio/all'](EPINIO_TYPES.SERVICE_INSTANCE) || [])
    .filter((s: EpinioService) => s.metadata.namespace === props.application.metadata.namespace)
);

const namespacedConfigurations = computed(() =>
  (store.getters['epinio/all'](EPINIO_TYPES.CONFIGURATION) || [])
    .filter((s: EpinioConfiguration) => s.metadata.namespace === props.application.metadata.namespace)
);

const configurations = computed(() => {
  const list = namespacedConfigurations.value
    .filter((s: EpinioConfiguration) => !s.isServiceRelated)
    .map((s: EpinioConfiguration) => ({
      label: s.metadata.name,
      value: s.metadata.name,
    }));

  return sortBy(list, 'label', false);
});

const services = computed(() => {
  const list = namespacedServices.value.map((s: EpinioService) => ({
    label: `${s.metadata.name} (${s.catalog_service})`,
    value: s.id,
  }));

  return sortBy(list, 'label', false);
});

const noConfigs = computed(() => !configurations.value.length);
const hasConfigs = computed(() => !!configurations.value.length);
const noServices = computed(() => !services.value.length);
const hasServices = computed(() => {
  return services.value.length > 0;
});
const isView = computed(() => props.mode === _VIEW);
const isFromManifest = computed(
  () => store.$router.currentRoute._value.query.from === EPINIO_APP_MANIFEST
);

// Watchers
watch(values, () => {
  emit('change', {
    configurations: values.value.configurations,
    services: values.value.services.map((s: string) => namespacedServices.value.find((ns: any) => ns.id === s)),
  })
}, { deep: true });

watch(noConfigs, (neu) => {
  if (neu && values.value.configurations?.length) {
    values.value.configurations = [];
  }
});

watch(noServices, (neu) => {
  if (neu && values.value.services?.length) {
    values.value.services = [];
  }
});

watch(hasConfigs, (neu, old) => {
  if (!old && neu) {
    if (props.initialApplication?.configuration?.configurations) {
      values.value.configurations = props.initialApplication.baseConfigurationsNames?.filter(
        (cc: string) => configurations.value.find((c: any) => c.value === cc)
      ) || [];
    }

    if (isFromManifest.value) {
      values.value.configurations = namespacedConfigurations.value
        .filter((nc: any) =>
          props.application.configuration.configurations.includes(nc.metadata.name) &&
          !nc.isServiceRelated
        )
        .map((nc: any) => nc.metadata.name) || [];
    }
  }
}, { immediate: true });

watch(hasServices, (neu, old) => {
    if (!old && neu) {
      if (props.initialApplication?.configuration?.services) {
        const serviceNames = props.initialApplication.configuration.services; 
        values.value.services = namespacedServices.value
          .filter((s: any) => serviceNames.includes(s.metadata.name))
          .map((s: any) => s.id);
      }
    }

    if (isFromManifest.value) {
      const configurations = namespacedConfigurations.value
        .filter((nc: any) =>
          props.application.configuration.configurations.includes(nc.metadata.name) &&
          nc.isServiceRelated
        );
      values.value.services = namespacedServices.value
        .filter((s: any) => configurations.some((d: any) => s.metadata.name === d.configuration.origin))
        .map((elem: any) => elem.id);
    }
}, { immediate: true });
</script>

<template>
  <div class="configurations">
    <trailhand-dropdown
      :values="values.configurations"
      data-testid="epinio_app-configuration_configurations"
      :disabled="noConfigs || isView"
      :options="configurations"
      multiselect
      filterable
      :label="t('typeLabel.configurations', { count: 2})"
      :placeholder="noConfigs ? t('epinio.applications.steps.configurations.configurations.select.placeholderNoOptions') : t('epinio.applications.steps.configurations.configurations.select.placeholderWithOptions')"
      @dropdown-change="(e: CustomEvent) => values.configurations = e.detail.values"
      />
    <trailhand-dropdown
      :values="values.services"
      data-testid="epinio_app-configuration_services"
      :disabled="noServices || isView"
      :options="services"
      multiselect
      filterable
      :label="t('epinio.applications.steps.configurations.services.select.label')"
      :placeholder="noServices ? t('epinio.applications.steps.configurations.services.select.placeholderNoOptions') : t('epinio.applications.steps.configurations.services.select.placeholderWithOptions')"
      @dropdown-change="(e: CustomEvent) => values.services = e.detail.values"
    />
  </div>
</template>

<style lang='scss' scoped>
.configurations {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
</style>
